
import React, { useState, useEffect } from 'react';
import { Settings, Maximize, Minimize, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';

interface AgendaSettingsProps {
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  showWeekends: boolean;
  onToggleWeekends: () => void;
  onResetCalendar?: () => void;
  firstDay: number;
  onFirstDayChange: (day: number) => void;
  slotMinTime: string;
  onSlotMinTimeChange: (time: string) => void;
  slotMaxTime: string;
  onSlotMaxTimeChange: (time: string) => void;
  eventTextColor: string;
  onEventTextColorChange: (color: string) => void;
  currentView: string;
  viewDateRange: { start: string; end: string } | null;
  onYearChange: (delta: number) => void;
}

const AgendaSettings = ({
  isFullscreen,
  onToggleFullscreen,
  showWeekends,
  onToggleWeekends,
  onResetCalendar,
  firstDay,
  onFirstDayChange,
  slotMinTime,
  onSlotMinTimeChange,
  slotMaxTime,
  onSlotMaxTimeChange,
  eventTextColor,
  onEventTextColorChange,
  currentView,
  viewDateRange,
  onYearChange
}: AgendaSettingsProps) => {
  const { locale, t } = useLanguage();
  const isMobile = useIsMobile();

  const isYearView = currentView === 'multiMonthYear' || currentView === 'listYear';
  const formatFullDate = (date: Date) =>
    new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);

  const yearRangeText = React.useMemo(() => {
    if (!viewDateRange) return '';

    const startDate = new Date(viewDateRange.start);
    const year = startDate.getFullYear();
    const endDate = new Date(year, 11, 31);

    if (isYearView) {
      return year.toString();
    }

    return `${formatFullDate(startDate)} - ${formatFullDate(endDate)}`;
  }, [viewDateRange, locale, isYearView]);

  const [hourFormat, setHourFormat] = useState(() => {
    const savedFormat = localStorage.getItem('hourFormat');
    if (savedFormat) {
      return savedFormat;
    }
    return locale === 'en' ? '12' : '24';
  });

  useEffect(() => {
    localStorage.setItem('hourFormat', hourFormat);
    window.dispatchEvent(new Event('hourFormatChanged'));
  }, [hourFormat]);
  
  // List of available time slots
  const timeOptions = [
    '00:00', '01:00', '02:00', '03:00', '04:00', '05:00', 
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', 
    '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', 
    '18:00', '19:00', '20:00', '21:00', '22:00', '23:00',
  ];
  const timeOptions_end = [
    '00:00', '01:00', '02:00', '03:00', '04:00', '05:00', 
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', 
    '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', 
    '18:00', '19:00', '20:00', '21:00', '22:00', '23:00', '23:30', '23:59',
  ];
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="h-8 w-8 p-0">
          <Settings className="h-4 w-4" />
          <span className="sr-only">{t('agenda.settings')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <div className="space-y-4">
          <h4 className="font-medium">{t('agenda.calendarSettings')}</h4>
          
          {isYearView && (
            <div className="space-y-2">
              <Label>{t('agenda.currentYear')}</Label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => onYearChange(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {yearRangeText || t('agenda.dateRangeHint')}
                </div>
                <Button variant="outline" size="icon" onClick={() => onYearChange(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
          {!isMobile && (
            <Button 
              variant="outline" 
              onClick={onToggleFullscreen} 
              className="w-full justify-start"
            >
              {isFullscreen ? (
                <>
                  <Minimize className="h-4 w-4 mr-2" />
                  {t('agenda.showSidebar')}
                </>
              ) : (
                <>
                  <Maximize className="h-4 w-4 mr-2" />
                  {t('agenda.hideSidebar')}
                </>
              )}
            </Button>
             )}
            
            <div className="space-y-3">
              <Label htmlFor="first-day">{t('agenda.firstDayOfWeek')}</Label>
              <Select 
                value={String(firstDay)} 
                onValueChange={(value) => onFirstDayChange(Number(value) as 0 | 1)}
              >
                <SelectTrigger id="first-day">
                  <SelectValue placeholder={t('agenda.selectFirstDay')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">{t('agenda.sunday')}</SelectItem>
                  <SelectItem value="1">{t('agenda.monday')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex space-x-4">
              <div className="space-y-3">
                <Label htmlFor="slot-min-time">{t('agenda.dayStartTime')}</Label>
                <Select value={slotMinTime} onValueChange={onSlotMinTimeChange}>
                  <SelectTrigger id="slot-min-time">
                    <SelectValue placeholder={t('agenda.selectStartTime')} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map((time) => (
                      <SelectItem key={`min-${time}`} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label htmlFor="slot-max-time">{t('agenda.dayEndTime')}</Label>
                <Select value={slotMaxTime} onValueChange={onSlotMaxTimeChange}>
                  <SelectTrigger id="slot-max-time">
                    <SelectValue placeholder={t('agenda.selectEndTime')} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions_end.map((time) => (
                      <SelectItem key={`max-${time}`} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="hour-format">{t('agenda.hourFormat', 'Hour Format')}</Label>
              <Select value={hourFormat} onValueChange={setHourFormat}>
                <SelectTrigger id="hour-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12 {t('agenda.hours')}</SelectItem>
                  <SelectItem value="24">24 {t('agenda.hours')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/*<div className="space-y-1">
              <Label htmlFor="event-text-color">{t('agenda.eventTextColor') || 'Event Text Color'}</Label>
              <Select value={eventTextColor} onValueChange={onEventTextColorChange}>
                <SelectTrigger id="event-text-color">
                  <SelectValue placeholder={t('agenda.selectTextColor') || 'Select text color'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dark">
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-gray-900 mr-2"></div>
                      {t('agenda.dark') || 'Dark'}
                    </div>
                  </SelectItem>
                  <SelectItem value="light">
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-gray-100 border border-gray-300 mr-2"></div>
                      {t('agenda.light') || 'Light'}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>*/}
            
            <div className="flex items-center justify-between space-y-3">
              <Label htmlFor="show-weekends" className="cursor-pointer">
                {t('agenda.showWeekends')}
              </Label>
              <Switch 
                id="show-weekends" 
                checked={showWeekends} 
                onCheckedChange={onToggleWeekends}
              />
            </div>

            {/*onResetCalendar && (
              <Button 
                variant="outline" 
                onClick={onResetCalendar} 
                className="w-full justify-start"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('agenda.resetCalendar')}
              </Button>
            )*/}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AgendaSettings;
